// Timed dispatch (G-59, G-67–G-70; spec 2026-10-12 fleet timing): orders, reservations, reinforce
// options and the alliance's shared attacks.
import { z } from 'zod';
import {
  idSchema,
  isoDateTimeSchema,
  resourcesSchema,
  shipCountsSchema,
  shipTypeSchema,
} from './common.js';
import { dispatchRequestSchema, fleetMissionSchema } from './fleets.js';

export const fleetOrderRequestSchema = dispatchRequestSchema.extend({
  /** The arrival the player wants; the server derives the departure from the route. */
  arriveAt: isoDateTimeSchema,
});
export type FleetOrderRequest = z.input<typeof fleetOrderRequestSchema>;

export const fleetOrderSchema = z.strictObject({
  id: idSchema,
  originSystemId: idSchema,
  targetSystemId: idSchema,
  mission: fleetMissionSchema,
  ships: shipCountsSchema,
  cargo: resourcesSchema,
  forceDirect: z.boolean(),
  shared: z.boolean(),
  /** The time the player chose. */
  arriveAt: isoDateTimeSchema,
  departAt: isoDateTimeSchema,
  /** Equals `arriveAt` unless re-planning (G-69) made the fleet late. */
  expectedArriveAt: isoDateTimeSchema,
  createdAt: isoDateTimeSchema,
});
export type FleetOrder = z.infer<typeof fleetOrderSchema>;

export const fleetOrderListResponseSchema = z.strictObject({
  items: z.array(fleetOrderSchema),
  /** Ships held back per origin system by open orders (G-67). */
  reserved: z.array(z.strictObject({ systemId: idSchema, ships: shipCountsSchema })),
});
export type FleetOrderListResponse = z.infer<typeof fleetOrderListResponseSchema>;

export const fleetOrderResponseSchema = z.strictObject({ order: fleetOrderSchema });
export type FleetOrderResponse = z.infer<typeof fleetOrderResponseSchema>;

export const cancelFleetOrderResponseSchema = z.strictObject({
  cancelledId: idSchema,
  refunded: resourcesSchema,
});
export type CancelFleetOrderResponse = z.infer<typeof cancelFleetOrderResponseSchema>;

export const REINFORCE_MISSIONS = ['attack', 'raid'] as const;
export const reinforceMissionSchema = z.enum(REINFORCE_MISSIONS);
export type ReinforceMission = z.infer<typeof reinforceMissionSchema>;

export const reinforceOptionsRequestSchema = z.strictObject({
  targetSystemId: idSchema,
  mission: reinforceMissionSchema,
  arriveAt: isoDateTimeSchema,
});
export type ReinforceOptionsRequest = z.infer<typeof reinforceOptionsRequestSchema>;

export const reinforceOptionSchema = z.strictObject({
  systemId: idSchema,
  /** Garrison ships not reserved by open orders. */
  ships: shipCountsSchema,
  /** Per class that can still make it: the latest departure. Missing classes are too slow. */
  latestDeparture: z.partialRecord(shipTypeSchema, isoDateTimeSchema),
});
export type ReinforceOption = z.infer<typeof reinforceOptionSchema>;

export const reinforceOptionsResponseSchema = z.strictObject({
  /** Sending would lift the caller's beginner shield (asked before sending). */
  shieldWouldLift: z.boolean(),
  items: z.array(reinforceOptionSchema),
});
export type ReinforceOptionsResponse = z.infer<typeof reinforceOptionsResponseSchema>;

export const allianceAttackSchema = z.strictObject({
  kind: z.enum(['order', 'fleet']),
  id: idSchema,
  owner: z.strictObject({ playerId: idSchema, displayName: z.string() }),
  originSystemId: idSchema,
  targetSystemId: idSchema,
  mission: reinforceMissionSchema,
  arriveAt: isoDateTimeSchema,
  ships: shipCountsSchema,
});
export type AllianceAttack = z.infer<typeof allianceAttackSchema>;

export const allianceAttacksResponseSchema = z.strictObject({
  items: z.array(allianceAttackSchema),
});
export type AllianceAttacksResponse = z.infer<typeof allianceAttacksResponseSchema>;
